<template>
    <Modal
        title="审批进度"
        v-model="visible"
        :mask-closable="false"
        width="640"
        footer-hide
        @on-visible-change="onVisibleChange"
    >
        <div class="progress-panel" v-if="loading">
            <Spin fix></Spin>
        </div>
        <div class="progress-panel" v-else-if="hasContent">
            <div class="meta-row" v-if="contractName">
                <span class="meta-label">合同名称</span>
                <span class="meta-value">{{ contractName }}</span>
            </div>
            <div class="submit-time" v-if="submitTime">
                提交时间 {{ submitTime }}
            </div>
            <div class="status-line" v-if="statusText">
                当前状态：<span :class="statusClass">{{ statusText }}</span>
            </div>
            <div class="flow-title">审批流程</div>
            <div class="flow-list">
                <div
                    v-for="(node, index) in nodeList"
                    :key="node.id || index"
                    class="flow-node"
                    :class="getNodeClass(node)"
                >
                    <div class="node-left">
                        <div class="node-icon">
                            <Icon v-if="Number(node.status) === 1" type="md-checkmark" />
                            <Icon v-else-if="Number(node.status) === 2" type="md-close" />
                            <Icon v-else type="md-time" />
                        </div>
                        <div class="node-line" v-if="index < nodeList.length - 1"></div>
                    </div>
                    <div class="node-body">
                        <div class="node-head">
                            <div class="node-name">
                                {{ node.node_name || node.name || '审批节点' }}
                                <span class="node-status">- {{ getNodeStatusText(node) }}</span>
                            </div>
                            <div class="node-time" v-if="node.operated_at || node.finish_time">
                                {{ node.operated_at || node.finish_time }}
                            </div>
                        </div>
                        <div
                            v-for="(approver, aIdx) in getApprovers(node)"
                            :key="aIdx"
                            class="approver-row"
                        >
                            <div class="approver-name">{{ approver.name || approver.approver_name || '审批人' }}</div>
                            <div class="approver-result">
                                <span :class="getApproverStatusClass(approver.status)">
                                    {{ getApproverStatusText(approver) }}
                                </span>
                                <span class="approver-time" v-if="approver.operated_at || approver.time || approver.updated_at">
                                    {{ approver.operated_at || approver.time || approver.updated_at }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="fail-reason" v-if="failReason">
                {{ Number(ticketStatus) === 3 ? '撤销原因' : '驳回原因' }}：{{ failReason }}
            </div>
        </div>
        <div class="progress-empty" v-else>暂无审批进度</div>
    </Modal>
</template>

<script>
import { contractApproveDetail } from '@/api/interFace'

export default {
    name: 'ApprovalProgress',
    data() {
        return {
            visible: false,
            loading: false,
            recordId: '',
            contractName: '',
            submitTime: '',
            statusText: '',
            statusClass: '',
            ticketStatus: '',
            failReason: '',
            nodeList: []
        }
    },
    computed: {
        hasContent() {
            return this.nodeList.length > 0 || !!this.contractName
        }
    },
    methods: {
        open(recordId) {
            this.recordId = recordId
            this.visible = true
            this.fetchProgress()
        },
        onVisibleChange(val) {
            if (!val) {
                this.recordId = ''
                this.contractName = ''
                this.submitTime = ''
                this.statusText = ''
                this.statusClass = ''
                this.ticketStatus = ''
                this.failReason = ''
                this.nodeList = []
            }
        },
        fetchProgress() {
            if (!this.recordId) return
            this.loading = true
            contractApproveDetail({ id: this.recordId }).then(res => {
                this.parseProgress(res.data || {})
            }).catch(() => {
                this.contractName = ''
                this.submitTime = ''
                this.statusText = ''
                this.nodeList = []
                this.failReason = ''
            }).finally(() => {
                this.loading = false
            })
        },
        parseProgress(data) {
            this.contractName = data.contract_name || ''
            this.submitTime = data.created_at || data.submit_time || ''
            this.ticketStatus = data.status
            this.statusText = data.status_text || ''
            this.statusClass = this.mapStatusClass(data.status)
            this.failReason = data.fail_reason || ''
            const nodes = data.records || data.nodes || data.node_list || data.list || []
            this.nodeList = Array.isArray(nodes) ? nodes : []
        },
        mapStatusClass(status) {
            const s = Number(status)
            if (s === 1) return 'text-passed'
            if (s === 2 || s === 4) return 'text-rejected'
            if (s === 3) return 'text-pending'
            return 'text-pending'
        },
        getApprovers(node) {
            if (Array.isArray(node.approvers) && node.approvers.length) {
                return node.approvers
            }
            return []
        },
        getNodeClass(node) {
            const status = Number(node.status)
            if (status === 1) return 'is-passed'
            if (status === 2) return 'is-rejected'
            return 'is-pending'
        },
        getNodeStatusText(node) {
            if (node.status_text) return node.status_text
            const status = Number(node.status)
            if (status === 1) return '已通过'
            if (status === 2) return '已驳回'
            return '待审批'
        },
        getApproverStatusText(approver) {
            if (approver.status_text) return approver.status_text
            const s = Number(approver.status)
            if (s === 1) return '已通过'
            if (s === 2) return '已驳回'
            return '待审批'
        },
        getApproverStatusClass(status) {
            const s = Number(status)
            if (s === 1) return 'text-passed'
            if (s === 2) return 'text-rejected'
            return 'text-pending'
        }
    }
}
</script>

<style scoped lang="scss">
.progress-panel {
    position: relative;
    min-height: 160px;
    padding: 4px 0 8px;
}

.meta-row {
    display: flex;
    margin-bottom: 10px;
    font-size: 14px;
    .meta-label {
        color: #8A93A6;
        margin-right: 12px;
        flex-shrink: 0;
    }
    .meta-value {
        color: #1B2742;
        word-break: break-all;
    }
}

.submit-time {
    font-size: 13px;
    color: #8A93A6;
    margin-bottom: 8px;
}

.status-line {
    font-size: 13px;
    color: #545F78;
    margin-bottom: 16px;
}

.flow-title {
    font-size: 15px;
    font-weight: 600;
    color: #1B2742;
    margin-bottom: 20px;
}

.flow-list {
    padding-left: 4px;
}

.flow-node {
    display: flex;
    margin-bottom: 4px;

    &.is-passed .node-icon {
        background: #4877E8;
        border-color: #4877E8;
        color: #fff;
    }

    &.is-rejected .node-icon {
        background: #ed4014;
        border-color: #ed4014;
        color: #fff;
    }

    &.is-pending .node-icon {
        background: #fff;
        border-color: #C5CAD6;
        color: #C5CAD6;
    }
}

.node-left {
    width: 32px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
}

.node-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid #4877E8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    z-index: 1;
    background: #fff;
}

.node-line {
    flex: 1;
    width: 2px;
    min-height: 24px;
    background: #E6EAF2;
    margin: 4px 0;
}

.node-body {
    flex: 1;
    padding: 0 0 20px 12px;
    min-width: 0;
}

.node-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.node-name {
    font-size: 14px;
    font-weight: 500;
    color: #1B2742;

    .node-status {
        font-weight: 400;
        color: #545F78;
    }
}

.node-time {
    font-size: 12px;
    color: #8A93A6;
    white-space: nowrap;
    flex-shrink: 0;
}

.approver-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 13px;
    color: #545F78;
}

.approver-name {
    flex: 1;
    min-width: 0;
}

.approver-result {
    text-align: right;
    flex-shrink: 0;
    margin-left: 12px;
}

.approver-time {
    margin-left: 8px;
    font-size: 12px;
    color: #8A93A6;
}

.text-passed {
    color: #4877E8;
}

.text-rejected {
    color: #ed4014;
}

.text-pending {
    color: #8A93A6;
}

.fail-reason {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #F0F2F5;
    font-size: 13px;
    color: #ed4014;
    line-height: 1.5;
}

.progress-empty {
    text-align: center;
    color: #8A93A6;
    padding: 48px 0;
    font-size: 14px;
}
</style>
